/**
 * DeviceAssignDrawer — Searchable device picker for assigning a device to the resident.
 *
 * Mirrors the Area Profile DeviceAssignDrawer pattern:
 *   - Only shows devices where is_area_device is false/0
 *   - Excludes devices already assigned to THIS resident
 *   - Sorts: unassigned first, then assigned-to-other-residents
 *   - Shows "Assigned to {resident_name}" indicator for devices on other residents
 *   - Requires confirmation before reassigning from another resident
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BlockedReasonTip from '../../../../../components/v2/BlockedReasonTip.jsx';
import DeviceNameLink from '../../../../../components/v2/DeviceNameLink.jsx';
import { DEVICE_ALARM_BLOCKED_MSG } from '../../../../../utils/activeAlerts.js';
import Drawer from '../../../../../components/v2/Drawer.jsx';


const DeviceAssignDrawer = ({
  open, onClose, onAssign, saving,
  availableDevices, resDeviceData, residentName,
  onAddNewDevice,
  /* Device ids with an active alarm, campus-wide. Reassigning a device mid-alarm can
     stop its clear signal being processed — and the one on offer may be alarming for
     another resident — so Assign/Reassign is disabled for it, as in ResidentsPage's drawer. */
  alertedDeviceIds,
}) => {
  // Ids compare as strings: the subscription delivers them as numbers on one path.
  const isAlarming = (d) => Boolean(alertedDeviceIds?.has(String(d?.device_id)));
  const [search, setSearch] = useState('');
  const [confirmDevice, setConfirmDevice] = useState(null);

  // Reset state when drawer opens
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setSearch('');
      setConfirmDevice(null);
    }
    prevOpen.current = open;
  });

  /**
   * Filtered & sorted available devices:
   * 1. Only include non-area devices (is_area_device is false/0)
   * 2. Exclude devices already assigned to THIS resident
   * 3. Apply search filter
   * 4. Sort: unassigned first, then assigned-to-other-residents
   */
  const assignedIds = useMemo(() => {
    return new Set((resDeviceData || []).map((d) => d.device_id));
  }, [resDeviceData]);

  const filteredDevices = useMemo(() => {
    const term = search.toLowerCase().trim();

    return (availableDevices || [])
      .filter((d) => {
        // Only non-area devices (is_area_device can be 1/0 or true/false)
        if (Number(d.is_area_device)) return false;
        // Exclude devices already assigned to THIS resident
        if (assignedIds.has(d.device_id)) return false;
        // Search filter
        if (term) {
          return (
            (d.device_name || '').toLowerCase().includes(term) ||
            (d.manufacture_uuid || '').toLowerCase().includes(term) ||
            (d.descriptions || '').toLowerCase().includes(term) ||
            (d.model_name || '').toLowerCase().includes(term) ||
            (d.resident_name || '').toLowerCase().includes(term)
          );
        }
        return true;
      })
      .sort((a, b) => {
        // Unassigned first, then assigned-to-other-residents
        const aAssigned = (a.resident_id && Number(a.is_active)) ? 1 : 0;
        const bAssigned = (b.resident_id && Number(b.is_active)) ? 1 : 0;
        if (aAssigned !== bAssigned) return aAssigned - bAssigned;
        return (a.device_name || '').localeCompare(b.device_name || '');
      });
  }, [availableDevices, assignedIds, search]);

  /** Handle assign click — if device is on another resident, show confirmation */
  const handleAssign = useCallback((device) => {
    if (isAlarming(device)) return;
    if (device.resident_id && Number(device.is_active)) {
      setConfirmDevice(device);
      return;
    }
    onAssign(device);
    // alertedDeviceIds listed so the alarm test never reads a stale set.
  }, [onAssign, alertedDeviceIds]);

  /** Confirm reassignment from another resident */
  const handleConfirmReassign = useCallback(() => {
    if (!confirmDevice) return;
    // An alarm can start while the confirm banner is open.
    if (isAlarming(confirmDevice)) return;
    onAssign(confirmDevice);
    setConfirmDevice(null);
  }, [confirmDevice, onAssign, alertedDeviceIds]);

  /** Cancel reassignment */
  const handleCancelReassign = useCallback(() => {
    setConfirmDevice(null);
  }, []);

  return (
    <Drawer
        open={open}
        onClose={onClose}
        title="Assign Device"
        backdrop={false}
        footer={(
            <>
                <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
                  Close
                </button>
            </>
        )}
        subheader={(
            <>
                {/* Sticky area: search + reassign confirmation */}
                      <div className="dp-drawer-sticky">
                        <div className="dp-drawer__field">
                          <label className="dp-drawer__label">Search Devices</label>
                          <input
                            type="text"
                            className="dp-drawer__input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Filter by name, serial, or description…"
                            autoFocus
                          />
                        </div>

                        {/* Reassign confirmation banner */}
                        {confirmDevice ? (
                          <div className="pp-reassign-confirm">
                            <div className="pp-reassign-confirm__message">
                              <svg className="pp-reassign-confirm__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                              </svg>
                              <div>
                                <p className="pp-reassign-confirm__title">Reassign device?</p>
                                <p className="pp-reassign-confirm__detail">
                                  <strong>{confirmDevice.device_name}</strong> is currently assigned to{' '}
                                  <strong>{confirmDevice.resident_name || 'another resident'}</strong>. Reassign to{' '}
                                  <strong>{residentName}</strong>?
                                </p>
                              </div>
                            </div>
                            <div className="pp-reassign-confirm__actions">
                              <button
                                type="button"
                                className="dp-btn dp-btn--cancel dp-btn--sm"
                                onClick={handleCancelReassign}
                                disabled={saving}
                              >
                                Cancel
                              </button>
                              <BlockedReasonTip message={isAlarming(confirmDevice) ? DEVICE_ALARM_BLOCKED_MSG : ''}>
                                <button
                                  type="button"
                                  className="dp-btn dp-btn--warning dp-btn--sm"
                                  onClick={handleConfirmReassign}
                                  disabled={saving || isAlarming(confirmDevice)}
                                >
                                  {saving ? 'Reassigning…' : 'Reassign'}
                                </button>
                              </BlockedReasonTip>
                            </div>
                          </div>
                        ) : null}

                        <label className="dp-drawer__label">
                          Available Devices ({filteredDevices.length})
                        </label>
                      </div>

                      {/* Scrollable device list */}
            </>
        )}
    >
        {/* Persistent so a brand-new device can be added without having to
            search and fail first. */}
        {onAddNewDevice ? (
          <button
            type="button"
            onClick={onAddNewDevice}
            className="dp-btn dp-btn--primary"
            style={{ width: '100%', marginBottom: 'var(--space-3)' }}
          >
            <i className="fe fe-plus" aria-hidden="true" style={{ marginRight: 6 }} />
            Add New Device
          </button>
        ) : null}

        <div className="pp-device-picker">
          {filteredDevices.length === 0 ? (
            <div className="pp-device-picker__empty">
              <p style={{ margin: 0 }}>
                {search.trim()
                  ? "No devices match your search. If it isn't in the system yet, add it here."
                  : 'No unassigned resident devices available.'}
              </p>
              {/* Points back at the quick-add flow rather than off to the Devices
                  page — leaving the resident was the confusion that flow removes.
                  The role-gated fallback to advanced setup lives inside it. */}
              {onAddNewDevice ? null : (
                <p style={{ margin: 'var(--space-2) 0 0' }}>
                  Can&apos;t find the device you&apos;re looking for?{' '}
                  <Link to="/community/devices" state={{ openAddDevice: true, addDeviceBase: 'resident' }} style={{ color: 'var(--color-primary)', fontWeight: 'var(--font-weight-semibold)' }}>
                    Add a device
                  </Link>
                </p>
              )}
            </div>
          ) : null}
          {filteredDevices.map((device) => {
            const isAssignedToOther = Boolean(device.resident_id && Number(device.is_active));
            const alarming = isAlarming(device);
            return (
              <div
                key={device.device_id}
                className={`pp-device-picker__item${isAssignedToOther ? ' pp-device-picker__item--assigned' : ''}`}
              >
                <div className="pp-device-picker__info">
                  <div className="pp-device-picker__name">
                    <DeviceNameLink device={device} className="pp-device-picker__link" />
                  </div>
                  <div className="pp-device-picker__meta">
                    {device.inovonics_model_name || device.model_name ? <span>{device.inovonics_model_name || device.model_name}</span> : null}
                    {(device.inovonics_model_name || device.model_name) && device.manufacture_uuid ? <span> · </span> : null}
                    {device.manufacture_uuid ? <span>{device.manufacture_uuid}</span> : <span>—</span>}
                  </div>
                  {device.descriptions ? (
                    <div className="pp-device-picker__meta">{device.descriptions}</div>
                  ) : null}
                  {isAssignedToOther ? (
                    <div className="pp-device-picker__assigned-to">
                      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                      </svg>
                      Assigned to {device.resident_name || 'another resident'}
                    </div>
                  ) : null}
                  {alarming ? (
                    <p className="rp-drawer-item__alarm">
                      <span className="rp-drawer-item__alarm-dot" aria-hidden="true" />
                      In alarm
                    </p>
                  ) : null}
                </div>
                <BlockedReasonTip message={alarming ? DEVICE_ALARM_BLOCKED_MSG : ''}>
                <button
                  type="button"
                  className={`pp-device-picker__btn ${isAssignedToOther ? 'pp-device-picker__btn--reassign' : 'pp-device-picker__btn--assign'}`}
                  onClick={() => handleAssign(device)}
                  disabled={saving || alarming}
                  title={alarming ? undefined : (isAssignedToOther
                    ? `Reassign ${device.device_name} from ${device.resident_name || 'another resident'}`
                    : `Assign ${device.device_name}`)
                  }
                >
                  {isAssignedToOther ? (
                    <>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                      </svg>
                      Reassign
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Assign
                    </>
                  )}
                </button>
                </BlockedReasonTip>
              </div>
            );
          })}
        </div>
    </Drawer>
  );
};

export default DeviceAssignDrawer;
